
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { AllVacationsHeader } from "./AllVacationsHeader";
import { VacationFilters } from "./VacationFilters";
import { AllVacationsContent } from "./AllVacationsContent";

interface AllVacationsProps {
  onBack: () => void;
  user?: User | null;
  favorites?: number[];
  onFavoriteChange?: () => void;
  searchTerm?: string;
}

export const AllVacations = ({ onBack, user = null, favorites = [], onFavoriteChange = () => {}, searchTerm = "" }: AllVacationsProps) => {
  const [vacations, setVacations] = useState<any[]>([]);
  const [displayCount, setDisplayCount] = useState(20);
  const [loading, setLoading] = useState(true);
  const [countryFilter, setCountryFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [useSlider, setUseSlider] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const [internalSearchTerm, setInternalSearchTerm] = useState(searchTerm);

  const hasMoreResults = displayCount < vacations.length;
  const displayedVacations = vacations.slice(0, displayCount);

  const { shouldLoadMore, resetLoadMore } = useInfiniteScroll({
    hasNextPage: hasMoreResults,
    isLoading: loading
  });

  useEffect(() => {
    if (shouldLoadMore) {
      setDisplayCount(prev => Math.min(prev + 20, vacations.length));
      resetLoadMore();
    }
  }, [shouldLoadMore, vacations.length, resetLoadMore]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const sortVacationsByPriceAvailability = (vacations: any[]) => {
    return vacations.sort((a, b) => {
      const aHasPrice = a.price !== null && a.price !== undefined && a.price > 0;
      const bHasPrice = b.price !== null && b.price !== undefined && b.price > 0;
      
      // Vacations with known prices come first
      if (aHasPrice && !bHasPrice) return -1;
      if (!aHasPrice && bHasPrice) return 1;
      
      // If both have prices or both don't have prices, maintain original order
      return 0;
    });
  };

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('Complete feed')
        .select('country')
        .not('country', 'is', null);
      
      if (error) throw error;
      
      const uniqueCountries = [...new Set(data.map(item => item.country))].sort();
      setCountries(uniqueCountries);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchVacations = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null);

      // Add search functionality
      if (internalSearchTerm.trim()) {
        const searchTerms = internalSearchTerm.toLowerCase().split(' ');
        const searchConditions: string[] = [];
        
        searchTerms.forEach(term => {
          searchConditions.push(
            `name.ilike.%${term}%`,
            `description.ilike.%${term}%`,
            `city.ilike.%${term}%`,
            `country.ilike.%${term}%`,
            `region.ilike.%${term}%`,
            `facilities.ilike.%${term}%`,
            `categoryPath.ilike.%${term}%`,
            `specialCategories.ilike.%${term}%`,
            `typeAccommodatie.ilike.%${term}%`,
            `typeVakantie.ilike.%${term}%`
          );
        });

        if (searchConditions.length > 0) {
          query = query.or(searchConditions.join(','));
        }
      }

      if (countryFilter !== "all") {
        query = query.eq('country', countryFilter);
      }

      if (useSlider) {
        query = query.gte('price', priceRange[0]).lte('price', priceRange[1]);
      } else if (priceFilter === 'budget') {
        query = query.lt('price', 300);
      } else if (priceFilter === 'low') {
        query = query.gte('price', 300).lt('price', 600);
      } else if (priceFilter === 'medium') {
        query = query.gte('price', 600).lt('price', 1200);
      } else if (priceFilter === 'high') {
        query = query.gte('price', 1200).lt('price', 2500);
      } else if (priceFilter === 'luxury') {
        query = query.gte('price', 2500);
      }

      const { data, error } = await query.limit(200);
      
      if (error) throw error;
      
      // Apply sorting with price preference
      const sortedData = sortVacationsByPriceAvailability(data || []);
      
      console.log('All vacations data loaded from Complete feed with price preference:', sortedData?.length || 0, 'items');
      console.log('Search term used:', internalSearchTerm);
      setVacations(sortedData);
    } catch (error: any) {
      toast.error("Fout bij het laden van vakanties");
      console.error('Error fetching vacations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchVacations();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [countryFilter, priceFilter, priceRange, useSlider, internalSearchTerm]);

  // Update internal search term when prop changes
  useEffect(() => {
    setInternalSearchTerm(searchTerm);
  }, [searchTerm]);

  return (
    <div className="space-y-6">
      <AllVacationsHeader onBack={onBack} />

      <VacationFilters 
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        priceFilter={priceFilter}
        setPriceFilter={setPriceFilter}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        useSlider={useSlider}
        setUseSlider={setUseSlider}
        countries={countries}
        searchTerm={internalSearchTerm}
        setSearchTerm={setInternalSearchTerm}
      />
      
      <AllVacationsContent 
        loading={loading}
        aiSearchLoading={false}
        displayedVacations={displayedVacations}
        vacations={vacations}
        hasMoreResults={hasMoreResults}
        user={user}
        favorites={favorites}
        onFavoriteChange={onFavoriteChange}
      />
    </div>
  );
};
